import { FC } from 'react';
import { useTranslate } from 'hooks/useTranslate';
import { Modal } from 'react-bootstrap';

import constitutionModalSteps from '../constants/constitutionModalSteps';
import { useConstitutionModal } from '../context/ConstitutionModalContext';

const ConstitutionModalFooter: FC = () => {
  const { t } = useTranslate();
  const { data, actions } = useConstitutionModal();

  const hasValidDate = data.date && data.date >= data.lastOperationDate;
  const previewErrorsCount = data.excelPreview?.errors.length || 0;
  const hasPreviewErrors = previewErrorsCount > 0;

  const buttonEnabledScriptureView =
    data.step === constitutionModalSteps.SCRIPTURE &&
    hasValidDate &&
    data.nominalValue > 0 &&
    ((!data.isExcelMode && data.totalSharesToAdd > 0) ||
      (data.isExcelMode && Boolean(data.excelPreview?.items.length)));

  const buttonEnabledSaveMovement =
    data.sharesToAdd > 0 &&
    data.remainingShares >= data.sharesToAdd &&
    data.selectedPartner &&
    data.selectedHoldingClass &&
    !data.isValidateRangeEnabled;

  const buttonEnabledMovementsView =
    data.step === constitutionModalSteps.MOVEMENTS &&
    data.remainingShares === 0 &&
    data.sharesToAdd === 0;

  const buttonEnabledPreviewView =
    data.step === constitutionModalSteps.PREVIEW &&
    hasValidDate &&
    data.nominalValue > 0 &&
    Boolean(data.excelPreview?.items.length) &&
    !hasPreviewErrors;

  const getPrimaryButtonText = () => {
    if (data.step === constitutionModalSteps.SCRIPTURE && data.isExcelMode) {
      return t('ConstitutionExcelPreview');
    }
    if (data.step === constitutionModalSteps.SCRIPTURE) return t('Next');
    if (data.step === constitutionModalSteps.PREVIEW) {
      return t('ExecuteConstitutionOperation');
    }
    return t('SaveConstitution');
  };

  const primaryButtonText = getPrimaryButtonText();

  const handlePrimaryAction = () => {
    if (data.step === constitutionModalSteps.PREVIEW) {
      actions.handleExecuteFromExcel();
      return;
    }

    actions.handleSaveConstitution();
  };

  const isPrimaryDisabled =
    !buttonEnabledScriptureView &&
    !buttonEnabledMovementsView &&
    !buttonEnabledPreviewView;

  const showPreviewErrorHint =
    data.step === constitutionModalSteps.PREVIEW && hasPreviewErrors;

  return (
    <Modal.Footer>
      <div className="d-flex justify-content-between align-items-center w-100">
        <div>
          {data.step === constitutionModalSteps.PREVIEW && (
            <button
              type="button"
              className="btn btn-lg btn-outline-light"
              onClick={actions.handleBackToScripture}
              disabled={data.isLoading}
            >
              {t('BackToScripture')}
            </button>
          )}

          {data.step === constitutionModalSteps.MOVEMENTS && (
            <button
              type="button"
              className="btn btn-lg btn-primary"
              onClick={actions.handleSaveMovement}
              disabled={!buttonEnabledSaveMovement}
            >
              {t('SaveMovement')}
            </button>
          )}

          {showPreviewErrorHint && (
            <span className="text-soft small ml-2" data-testid="constitution-preview-error-hint">
              {t('ConstitutionCorrectExcelToContinue')}
            </span>
          )}
        </div>

        <button
          type="button"
          className="btn btn-lg btn-primary"
          onClick={handlePrimaryAction}
          disabled={isPrimaryDisabled || data.isLoading || data.isParsingExcel}
          data-testid="constitution-modal-primary-button"
        >
          {primaryButtonText}
        </button>
      </div>
    </Modal.Footer>
  );
};

export default ConstitutionModalFooter;
